import React, { useState } from 'react';
import { ArrowRight, Check, RotateCcw, HelpCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
type Step = {
  id: number;
  question: string;
  options: {
    label: string;
    value: string;
  }[];
};
const steps: Step[] = [{
  id: 1,
  question: 'What are you finishing?',
  options: [{
    label: 'Driveway',
    value: 'driveway'
  }, {
    label: 'Walkway or Steps',
    value: 'walkway'
  }, {
    label: 'Patio',
    value: 'patio'
  }, {
    label: 'Multiple Areas',
    value: 'multiple'
  }]
}, {
  id: 2,
  question: 'Is there a slope?',
  options: [{
    label: 'Flat',
    value: 'flat'
  }, {
    label: 'Slight Slope',
    value: 'slight'
  }, {
    label: 'Steep Slope',
    value: 'steep'
  }]
}, {
  id: 3,
  question: 'How important is appearance?',
  options: [{
    label: 'Function First',
    value: 'function'
  }, {
    label: 'Nice but Practical',
    value: 'practical'
  }, {
    label: 'I want it to look amazing',
    value: 'amazing'
  }]
}, {
  id: 4,
  question: 'Maintenance tolerance?',
  options: [{
    label: 'Minimal (Set and forget)',
    value: 'minimal'
  }, {
    label: 'Some (Reseal every few years)',
    value: 'some'
  }, {
    label: 'Whatever it takes',
    value: 'high'
  }]
}];
export function FinishDecisionGuide() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showResult, setShowResult] = useState(false);
  const handleOptionSelect = (value: string) => {
    const newAnswers = {
      ...answers,
      [currentStep + 1]: value
    };
    setAnswers(newAnswers);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResult(true);
    }
  };
  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResult(false);
  };
  const getRecommendation = () => {
    const area = answers[1];
    const slope = answers[2];
    const appearance = answers[3];
    const maintenance = answers[4];
    if (area === 'multiple') {
      return {
        type: 'Custom Assessment Needed',
        description: 'Since you have multiple areas with different needs, we recommend mixing finishes. For example, a broom finish driveway for traction combined with a stamped patio for aesthetics.',
        warning: null
      };
    }
    if (area === 'driveway' && (slope === 'steep' || slope === 'slight') && appearance === 'function') {
      return {
        type: 'Broom Finish',
        description: 'For a sloped driveway where function is key, nothing beats a broom finish. It provides maximum traction for vehicles and feet in all weather conditions.',
        warning: null
      };
    }
    if (area === 'driveway' && slope === 'flat' && appearance === 'practical') {
      return {
        type: 'Exposed Aggregate',
        description: 'Exposed aggregate is a fantastic middle ground. It hides stains well, offers great traction, and looks more premium than plain concrete without the high maintenance of stamped.',
        warning: null
      };
    }
    if (area === 'patio' && slope === 'flat' && appearance === 'amazing') {
      return {
        type: 'Stamped Concrete (with Texture Additive)',
        description: "For a flat patio where looks matter most, stamped concrete is a great choice. We'll ensure a texture additive is used in the sealer to prevent slipping.",
        warning: 'Requires resealing every 2-3 years to maintain color and protection.'
      };
    }
    if (area === 'walkway' && slope === 'steep') {
      return {
        type: 'Broom Finish',
        description: 'Safety first on steep walkways. A broom finish gives the best grip.',
        warning: 'Avoid smooth or stamped finishes here—they are slip hazards on slopes.'
      };
    }
    if (maintenance === 'minimal') {
      return {
        type: 'Exposed Aggregate or Broom',
        description: "Both of these finishes age gracefully and don't require constant resealing to look good. They are the most practical choices for low maintenance.",
        warning: null
      };
    }
    // Default fallback
    return {
      type: 'Exposed Aggregate',
      description: 'This is our most versatile recommendation. It balances traction, aesthetics, and durability for most heated applications.',
      warning: null
    };
  };
  const recommendation = showResult ? getRecommendation() : null;
  return <section id="finish-guide" className="py-16 bg-white md:py-24" data-id="element-174">
      <div className="container mx-auto px-4 md:px-6" data-id="element-175">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-176">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-177">
            Find Your Best Finish in 60 Seconds
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-178">
            Answer 4 quick questions to get a personalized recommendation.
          </p>
        </div>

        <div className="max-w-2xl mx-auto" data-id="element-179">
          <Card className="shadow-xl border-slate-200 overflow-hidden" data-id="element-180">
            <div className="bg-slate-100 h-2 w-full" data-id="element-181">
              <div className="bg-primary h-full transition-all duration-500 ease-out" style={{
              width: `${(currentStep + 1) / steps.length * 100}%`
            }} data-id="element-182"></div>
            </div>

            <CardContent className="p-8 md:p-12" data-id="element-183">
              {!showResult ? <div className="animate-in fade-in slide-in-from-right-4 duration-300" key={currentStep} data-id="element-184">
                  <span className="text-sm font-semibold text-primary uppercase tracking-wider mb-2 block" data-id="element-185">
                    Question {currentStep + 1} of {steps.length}
                  </span>
                  <h3 className="text-2xl font-bold text-slate-900 mb-8" data-id="element-186">
                    {steps[currentStep].question}
                  </h3>

                  <div className="grid gap-4" data-id="element-187">
                    {steps[currentStep].options.map(option => <button key={option.value} onClick={() => handleOptionSelect(option.value)} className="text-left p-4 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all duration-200 flex items-center justify-between group" data-id="element-188">
                        <span className="font-medium text-slate-700 group-hover:text-primary" data-id="element-189">
                          {option.label}
                        </span>
                        <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-primary opacity-0 group-hover:opacity-100 transition-all" data-id="element-190" />
                      </button>)}
                  </div>
                </div> : <div className="text-center animate-in fade-in zoom-in-95 duration-500" data-id="element-191">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-192">
                    {recommendation?.type.includes('Custom') ? <HelpCircle className="h-8 w-8" data-id="element-193" /> : <Check className="h-8 w-8" data-id="element-194" />}
                  </div>

                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-195">
                    We Recommend
                  </h3>
                  <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-196">
                    {recommendation?.type}
                  </h2>

                  <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-197">
                    {recommendation?.description}
                  </p>

                  {recommendation?.warning && <div className="bg-amber-50 border border-amber-100 p-4 rounded-lg mb-8 inline-block text-left" data-id="element-198">
                      <div className="flex gap-2 items-start" data-id="element-199">
                        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" data-id="element-200" />
                        <p className="text-sm text-amber-800" data-id="element-201">
                          {recommendation.warning}
                        </p>
                      </div>
                    </div>}

                  <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-202">
                    <Button size="lg" onClick={() => {
                  const element = document.getElementById('quote-form');
                  if (element) element.scrollIntoView({
                    behavior: 'smooth'
                  });
                }} data-id="element-203">
                      Get a Quote for This Finish
                    </Button>
                    <Button variant="ghost" onClick={resetQuiz} className="text-slate-500 hover:text-slate-900" data-id="element-204">
                      <RotateCcw className="h-4 w-4 mr-2" data-id="element-205" /> Start Over
                    </Button>
                  </div>
                </div>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}